/* OsCatalogSkusRenderer.js */

"use strict";
const _ = require("lodash");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const MultipleSelectListControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Multiple.SelectList;

class OsCatalogSkusRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("os-catalog-skus-gl");
        this._formFieldset = document.createElement("fieldset");
        this._legend = document.createElement("legend");
        this._legend.innerHTML = "Device SKUs";
        this._skusCountContainer = document.createElement("skusCount");
        this._skusCount = 0;
        this._isDirty = false;
        this._formsComponent = new FormsComponent();
        this._renderingForm = false;
        this._detectedSkusSection = new MultipleSelectListControl({
            options: [
                {
                    label: "",
                    disabled: true,
                    value: { val: "" }
                }
            ]
        });
        this._logger = this.model._logger;
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(this._onFormsPendingPatchesChanged.bind(this), 500, false))
        );
    }

    _unregisterListeners() {
        this._formsComponent.removeListener("PendingPatchesChanged", this._onFormsPendingPatchesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    render() {
        return this._renderSkusSection().then(() => {
            this._formFieldset.append(this._legend, this._formsComponent.renderer.rootElement);
            this._rootElem.appendChild(this._formFieldset);
            this._glFormRendererElement = document.querySelector(".focused_item os-catalog-skus-gl gl-form-renderer");
            this._glFormRendererElement.append(this._detectedSkusSection.renderer.rootElement, this._skusCountContainer);
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    shutdown() {
        return this._onShutdown();
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderSkusSection() {
        return Promise.resolve().then(
            () => {
                let jType = this._getJtype();
                let pristineValue = this._getPristineValues();
                this._formsComponent.setTypeInformation(jType, true);
                this._formsComponent.setPristineValue(pristineValue, true);
                this._renderingForm = true;
                return this._formsComponent.renderer.render().then(() => {
                    this._renderingForm = false;
                });
            },
            (err) => {
                this._logger.warn({ error: err }, "OSCatalogSkusRenderer._renderLists() error.");
            }
        );
    }

    _renderDetectedSkusSection(convertedDetectedSkus) {
        this._detectedSkusSection.options = convertedDetectedSkus;
        this._detectedSkusSection.renderer.render();
        this._skusCount = convertedDetectedSkus.length;
        this._skusCountContainer.innerHTML = `${this._skusCount} Device SKUs`;
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this._unregisterListeners();
        return Promise.all([this._formsComponent.shutdown(), this._detectedSkusSection.shutdown()])
            .then(() => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._formsComponent = undefined;
                this._detectedSkusSection = undefined;
                this._formFieldset = undefined;
                this._legend = undefined;
                this._skusCountContainer = undefined;
                this._glFormRendererElement = undefined;
                this._renderingForm = undefined;
            })
            .catch((err) => {
                this._model._logger.warn({ error: err }, "OsCatalogSkusRenderer._onShutdown()");
            });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getPristineValues() {
        const pristineProperties = this.model.osCatalogEntity.getPristineProperties();
        let pristineValues = _.cloneDeep(pristineProperties);
        return pristineValues;
    }

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm && this._formsComponent) {
            this._setNewRowPlaceholder();
            const form = this._formsComponent.getValue();
            if (!_.isEqual(_.get(this.model.osCatalogEntity.getSkus(), "", []), form.skus)) {
                this.model.osCatalogEntity.setSkus(form.skus);
            }

            this._getDetectedSkus().then((convertedSkus = []) => {
                this._renderDetectedSkusSection(convertedSkus);
                return Promise.resolve();
            });
        }
    }

    _getDetectedSkus() {
        let query = this._getSkusRegexQuery() || [];
        if (query.length > 0) {
            return this.model.deviceEntity
                .loadDeviceDistinctValues(this.model.activeOrgId, "detectedSku", { $or: query })
                .then((detectedSkusList) => {
                    if (detectedSkusList) {
                        let convertedSkus = this._convertDetectedSkus(detectedSkusList);
                        return Promise.resolve(convertedSkus);
                    } else {
                        return Promise.resolve();
                    }
                })
                .catch((err) => {
                    this._model._logger.warn({ error: err }, "OsCatalogSkusRenderer._onShutdown()");
                });
        } else {
            return Promise.resolve();
        }
    }

    _convertDetectedSkus(detectedSkusList) {
        let convertedSkusList = detectedSkusList.map((item) => {
            return {
                label: item,
                value: { val: item }
            };
        });
        return convertedSkusList;
    }

    _getSkusRegexQuery() {
        if (this._formsComponent.getValue().skus) {
            const regexCollection = this._formsComponent.getValue().skus.reduce((newRegexCollection, value) => {
                let strRegExp = value.skusCol;
                if (strRegExp) {
                    let regexp = this._model.checkRegexpValidity(strRegExp, true);
                    if (regexp) {
                        newRegexCollection.push({
                            detectedSku: {
                                $regex: regexp.source,
                                $options: regexp.flags
                            }
                        });
                    }
                }
                return newRegexCollection;
            }, []);
            return regexCollection;
        }
    }

    _setNewRowPlaceholder() {
        let gridRowInput = document.querySelector(`
                            editor-layout-gl .focused_item 
                            os-catalog-details-view-gl os-catalog-skus-gl 
                            gl-form-renderer .rendered_control .grid-canvas .slick-cell.selected input
                            `);
        if (gridRowInput) {
            gridRowInput.setAttribute("placeholder", this._model._regexPlaceholder);
        }
    }

    _getJtype() {
        let isReadOnly = false;
        if (this._model._gameEditor.userAccessToPlan) {
            isReadOnly = true;
        }
        return {
            "skus@Display.table": {
                detailFormLabel: "My Row Details",
                showLineNumbers: false,
                showReorder: false,
                minLineCount: 9,
                allowEdit: !isReadOnly,
                showAdd: !isReadOnly,
                showDelete: !isReadOnly,
                showDetailForm: false,
                forceFitColumns: true,
                columns: {
                    skusCol: {
                        showSort: true,
                        label: "Regular Expression"
                    }
                }
            },
            skus: [
                {
                    skusCol: ""
                }
            ]
        };
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = OsCatalogSkusRenderer;
